/**
 * Detalle completo de una entrevista del Hiring Manager
 * Muestra toda la información del candidato y la entrevista
 */

import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React from 'react'
import { 
  X,
  User,
  Mail,
  Phone,
  Calendar,
  Clock,
  MapPin,
  Video,
  FileText,
  AlertCircle,
  Building2,
  Download
} from 'lucide-react'
import type { HiringManagerInterview } from '@/types/hiringManagerInterview'
import { hiringManagerInterviewService } from '@/features/rh/api/hiringManagerInterviewService'
import { fileService } from '@/services/fileService'
import toast from 'react-hot-toast'
import { nombreDeUsuario } from "@/shared/lib/nombreDeUsuario";
import { parseDateSafe, formatDateTimeMX } from '@/shared/lib/dateUtils'
import { Badge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface HiringManagerInterviewDetailProps {
  interview: HiringManagerInterview
  isOpen: boolean
  onClose: () => void
  onRecordDecision?: (interview: HiringManagerInterview) => void
}

export const HiringManagerInterviewDetail: React.FC<HiringManagerInterviewDetailProps> = ({
  interview,
  isOpen,
  onClose,
  onRecordDecision
}) => {
  if (!isOpen) return null

  const decisionColor = hiringManagerInterviewService.getDecisionColor(interview.decision)
  const decisionLabel = hiringManagerInterviewService.getDecisionLabel(interview.decision)

  return (
    <CapaDeModal onClickFondo={onClose}>
        <div className="w-full max-w-4xl overflow-hidden text-left bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Detalle de Entrevista
                  </h2>
                  <p className="text-blue-100 text-sm">
                    {interview.candidate.fullName}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {/* Estado de la entrevista */}
            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Estado de la Entrevista</h3>
                  <Badge color={decisionColor}>{decisionLabel}</Badge>
                </div>
                {interview.decision === 'pendiente' && onRecordDecision && (
                  <button
                    onClick={() => onRecordDecision(interview)}
                    className={estiloDeBoton('primario')}
                  >
                    Registrar Decisión
                  </button>
                )}
              </div>
            </div>

            {/* Información del Candidato */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Información del Candidato
              </h3>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Nombre Completo</p>
                      <p className="font-medium text-gray-900">{interview.candidate.fullName}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Correo Electrónico</p>
                      <p className="font-medium text-gray-900">{interview.candidate.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Teléfono</p>
                      <p className="font-medium text-gray-900">{interview.candidate.phone}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Estado del Candidato</p>
                      <p className="font-medium text-gray-900 capitalize">
                        {interview.candidate.status.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Información de la Entrevista */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Datos de la Entrevista
              </h3>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Fecha</p>
                      <p className="font-medium text-gray-900">
                        {parseDateSafe(interview.fechaEntrevista)?.toLocaleDateString('es-MX', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Hora</p>
                      <p className="font-medium text-gray-900">{interview.horaEntrevista}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {interview.modalidad === 'virtual' ? (
                      <Video className="w-5 h-5 text-gray-400" />
                    ) : (
                      <MapPin className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <p className="text-xs text-gray-500">Modalidad</p>
                      <p className="font-medium text-gray-900 capitalize">{interview.modalidad}</p>
                    </div>
                  </div>

                  {interview.ubicacionLink && (
                    <div className="flex items-center gap-3 md:col-span-2">
                      {interview.modalidad === 'virtual' ? (
                        <Video className="w-5 h-5 text-gray-400" />
                      ) : (
                        <MapPin className="w-5 h-5 text-gray-400" />
                      )}
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">
                          {interview.modalidad === 'virtual' ? 'Link de la Reunión' : 'Ubicación'}
                        </p>
                        {interview.modalidad === 'virtual' ? (
                          <a 
                            href={interview.ubicacionLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            {interview.ubicacionLink}
                          </a>
                        ) : (
                          <p className="font-medium text-gray-900">{interview.ubicacionLink}</p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3 md:col-span-2">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Entrevistador</p>
                      {/* Puede faltar: si la cuenta se dio de baja, la
                          entrevista conserva el nombre pero ya no hay usuario. */}
                      <p className="font-medium text-gray-900">
                        {nombreDeUsuario(
                          interview.entrevistador ?? interview.entrevistadorHistorico,
                        )}
                      </p>
                      {(interview.entrevistador?.email ??
                        interview.entrevistadorHistorico?.email) && (
                        <p className="text-sm text-gray-500">
                          {interview.entrevistador?.email ??
                            interview.entrevistadorHistorico?.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 md:col-span-2">
                    <Building2 className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">ÁrDepartamento/Áreaea</p>
                      <p className="font-medium text-gray-900">{interview.area.nombre}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notas del Área */}
            {interview.notaArea && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Notas del Departamento/Área
                </h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-gray-700">{interview.notaArea}</p>
                </div>
              </div>
            )}

            {/* Comentarios de la Decisión */}
            {interview.comentarios && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Comentarios de la Decisión
                </h3>
                <div className={`rounded-lg p-4 ${
                  interview.decision === 'aprobado' 
                    ? 'bg-green-50 border border-green-200'
                    : interview.decision === 'rechazado'
                    ? 'bg-red-50 border border-red-200'
                    : 'bg-gray-50 border border-gray-200'
                }`}>
                  <p className="text-gray-700">{interview.comentarios}</p>
                </div>
              </div>
            )}

            {/* Archivo de Resultados */}
            {interview.archivoResultados && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Archivo de Resultados
                </h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <button
                    onClick={async () => {
                      try {
                        const fileName = `Resultados_Entrevista_${interview.candidate.fullName.replace(/\s+/g, '_')}.pdf`
                        await fileService.downloadFile(interview.archivoResultados!, fileName)
                      } catch (error) {
                        console.error('Error downloading file:', error)
                        toast.error('Error al descargar el archivo')
                      }
                    }}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <Download className="w-5 h-5" />
                    Descargar Archivo de Resultados
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    Click para descargar el archivo adjunto
                  </p>
                </div>
              </div>
            )}

            {/* Metadatos */}
            <div className="pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
                <div>
                  <p className="font-medium">Creada</p>
                  <p>
                    {formatDateTimeMX(interview.createdAt)}
                  </p>
                </div>
                <div>
                  <p className="font-medium">Última actualización</p>
                  <p>
                    {formatDateTimeMX(interview.updatedAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
            <button
              onClick={onClose}
              className={estiloDeBoton('secundario')}
            >
              Cerrar
            </button>
            {interview.decision === 'pendiente' && onRecordDecision && (
              <button
                onClick={() => {
                  onRecordDecision(interview)
                  onClose()
                }}
                className={estiloDeBoton('primario')}
              >
                Registrar Decisión
              </button>
            )}
          </div>
        </div>
    </CapaDeModal>
  )
}
